// pages/setup/components/ScriptModal.tsx
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, Code } from 'lucide-react';

interface ScriptModalProps {
    isOpen: boolean;
    onClose: () => void;
    domainId: number;
}

export function ScriptModal({ isOpen, onClose, domainId }: ScriptModalProps) {
    const [copied, setCopied] = useState(false);

    const scriptCode = `<link rel="preload" href="https://script.rocketgroup.io/ad${domainId}.js" as="script" type="text/javascript">
<script type="module" src="https://script.rocketgroup.io/ad${domainId}.js" async></script>`;

    const handleCopy = () => {
        navigator.clipboard.writeText(scriptCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Code className="h-5 w-5 text-green-600" />
                        Script de Integração
                    </DialogTitle>
                    <DialogDescription>
                        Copie e cole este código no <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">&lt;head&gt;</code> do seu site
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    {/* Script Code */}
                    <div className="relative">
                        <pre className="bg-gray-900 text-gray-100 p-5 pt-10 rounded-lg overflow-x-auto text-sm font-mono">
                            {scriptCode}
                        </pre>
                        <Button
                            onClick={handleCopy}
                            size="sm"
                            variant="secondary"
                            className="text-white absolute top-2 right-2"
                        >
                            {copied ? (
                                <>
                                    <Check className="text-white h-4 w-4 mr-2" />
                                    Copiado!
                                </>
                            ) : (
                                <>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copiar
                                </>
                            )}
                        </Button>
                    </div>

                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Como integrar
                        </h4>
                        <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                            <li>Copie o código acima clicando no botão "Copiar"</li>
                            <li>Abra o arquivo HTML do seu site</li>
                            <li>Cole o código dentro da tag <code className="bg-blue-100 px-1 py-0.5 rounded">&lt;head&gt;</code></li>
                            <li>Salve e publique as alterações</li>
                        </ol>
                    </div>

                    {/* Example */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Exemplo de uso:</h4>
                        <pre className="bg-white text-gray-800 p-3 rounded border border-gray-300 overflow-x-auto text-xs font-mono">
{`<!DOCTYPE html>
<html>
<head>
  <title>Meu Site</title>
  
  <!-- Script -->
  <link rel="preload" href="https://script.rocketgroup.io/ad${domainId}.js" as="script" type="text/javascript">
  <script type="module" src="https://script.rocketgroup.io/ad${domainId}.js" async></script>
  <!-- Fim Script -->
  
</head>
<body>
  ...
</body>
</html>`}
                        </pre>
                    </div>

                    {/* Notes */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Importante:</h4>
                        <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                            <li>Certifique-se de ter gerado o script antes de integrar</li>
                            <li>O script deve ser inserido no header do site</li>
                            <li>Aguarde alguns minutos após a integração para os anúncios aparecerem</li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={onClose}>
                            Fechar
                        </Button>
                        <Button onClick={handleCopy} className="bg-gradient-to-r from-green-600 to-emerald-600">
                            {copied ? (
                                <>
                                    <Check className="h-4 w-4 mr-2" />
                                    Copiado!
                                </>
                            ) : (
                                <>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copiar Script
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}